import { getActionError } from "../utils/actionFeedback";
import { toast } from "react-toastify";
import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Description, Transition, TransitionChild } from "@headlessui/react";
import { FaArrowRight, FaCheck, FaLock, FaWallet, FaTimes } from "react-icons/fa";
import CryptoJS from "crypto-js";
import axiosInstance from "../Context/axiosInstance";
import { getFinalPrice } from "../utils/pricing";
import ScrollingCourseTitle from "./ScrollingCourseTitle";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

export default function CoursePurchaseModal({ course, currency = "EGP", isDarkMode, onClose, onPurchased, onStart }) {
  const [balance, setBalance] = useState(null);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [purchaseFailed, setPurchaseFailed] = useState(false);
  const reduceMotion = useReducedMotion();
  const [error, setError] = useState("");
  const [walletAttempt, setWalletAttempt] = useState(0);
  const submitting = useRef(false);
  const [visible, setVisible] = useState(true);
  const afterClose = useRef(null);
  const close = (next = onClose) => {
    if (submitting.current || !visible) return;
    afterClose.current = next;
    setVisible(false);
  };
  const price = getFinalPrice(course);
  const free = price === 0;
  const insufficient = balance !== null && balance < price;
  const money = (value) => `${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  const muted = isDarkMode ? "text-slate-400" : "text-slate-500";

  useEffect(() => {
    let active = true;
    const checkWallet = async () => {
      setChecking(true);
      setError("");
      try {
        const userId = CryptoJS.AES.decrypt(localStorage.getItem("userId") || "", process.env.REACT_APP_SECRET_KEY).toString(CryptoJS.enc.Utf8);
        if (!userId) throw new Error("Missing user");
        const { data } = await axiosInstance.get(`/users/${userId}/wallet`);
        if (data.balance == null || !Number.isFinite(Number(data.balance))) throw new Error("Invalid balance");
        if (active) setBalance(Number(data.balance));
      } catch {
        if (active) setError("We couldn't check your wallet. Please try again.");
      } finally {
        if (active) setChecking(false);
      }
    };
    if (free) setChecking(false);
    else checkWallet();
    return () => { active = false; };
  }, [free, walletAttempt]);

  const purchase = async () => {
    if (submitting.current || success || checking || (!free && (balance === null || insufficient))) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    setPurchaseFailed(false);
    try {
      await axiosInstance.post(`/courses/${course.id}/purchase`, { courseId: course.id });
      toast.success("Course purchased successfully.");
      setSuccess(true);
      onPurchased({ ...course, purchased: true }, balance === null ? null : Math.max(0, balance - price));
    } catch (err) {
      toast.error(getActionError(err, "Could not complete the purchase."));
      const detail = err.response?.data?.detail || err.response?.data?.message;
      setPurchaseFailed(true);
      setError(typeof detail === "string" ? detail : "We couldn't confirm your purchase. Please try again.");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Transition appear show={visible} as={Fragment} afterLeave={() => afterClose.current?.()}>
    <Dialog onClose={() => close()} className="relative z-[100]">
      <TransitionChild as={Fragment} enter="transition-opacity duration-200 ease-out motion-reduce:duration-0" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity duration-150 ease-in motion-reduce:duration-0" leaveFrom="opacity-100" leaveTo="opacity-0">
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" />
      </TransitionChild>
      <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
        <div className="flex min-h-full items-center justify-center">
          <TransitionChild as={Fragment} enter="transition duration-200 ease-out motion-reduce:duration-0" enterFrom="opacity-0 translate-y-4 scale-95 motion-reduce:transform-none" enterTo="opacity-100 translate-y-0 scale-100" leave="transition duration-150 ease-in motion-reduce:duration-0" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-95 motion-reduce:transform-none">
          <DialogPanel className={`h-[680px] max-h-[calc(100dvh-3rem)] w-full max-w-md overflow-hidden rounded-3xl border shadow-2xl ${isDarkMode ? "border-slate-700 bg-[var(--study-surface)] text-slate-100" : "border-white bg-white text-slate-900"}`}>
            <div className="relative flex h-full flex-col" aria-busy={busy}>
              <div className="min-h-0 flex-1 overflow-y-auto p-6 pb-2 sm:p-8 sm:pb-2">
              <button type="button" aria-label="Close purchase dialog" disabled={busy} onClick={() => close()} className={`absolute right-4 top-4 rounded-full p-3 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-40 ${muted}`}><FaTimes aria-hidden="true" /></button>
              <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${success ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                {success ? <FaCheck aria-hidden="true" /> : <FaWallet aria-hidden="true" />}
              </div>
              <DialogTitle className="pr-6 text-2xl font-bold tracking-tight">{success ? "You're ready to learn" : purchaseFailed ? "Purchase not confirmed" : free ? "Claim your free course" : "Confirm Purchase"}</DialogTitle>
              <Description className={`mt-2 text-sm leading-relaxed ${muted}`}>{success ? "Your course is unlocked. Start whenever you're ready." : purchaseFailed ? "Review the message below before trying again." : free ? "Add this course to your learning library for free." : "Review your course and pay with your wallet."}</Description>
              <div className={`mt-6 flex items-center gap-4 rounded-2xl border p-3 ${isDarkMode ? "border-slate-700 bg-white/5" : "border-slate-200 bg-slate-50"}`}>
                <img src={course.courseImageUrl || course.imageUrl || "/default-course.jpg"} alt="" className="h-16 w-20 shrink-0 rounded-xl object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <div className="min-w-0 flex-1"><p className={`text-xs font-medium ${muted}`}>Your course</p><ScrollingCourseTitle title={course.name} /></div>
              </div>
              <AnimatePresence mode="wait" initial={false}>
              {success || purchaseFailed ? (
                <motion.div
                  key={success ? "purchase-success" : "purchase-failure"}
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: reduceMotion ? 0 : -8 }}
                  transition={{ duration: reduceMotion ? 0 : 0.22 }}
                  role={success ? "status" : "alert"}
                  className={`mt-6 flex flex-col items-center rounded-2xl border px-4 py-6 text-center ${success ? isDarkMode ? "border-emerald-700/50 bg-emerald-900/20 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-800" : isDarkMode ? "border-red-700/50 bg-red-900/20 text-red-200" : "border-red-200 bg-red-50 text-red-800"}`}
                >
                  <motion.div
                    initial={{ scale: reduceMotion ? 1 : 0.65 }}
                    animate={{ scale: 1 }}
                    transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 260, damping: 16 }}
                    className={`mb-3 flex h-16 w-16 items-center justify-center rounded-full ${success ? "bg-emerald-500/15" : "bg-red-500/15"}`}
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 48 48" className="h-10 w-10" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <motion.path
                        d={success ? "M12 24l8 8 16-17" : "M15 15l18 18M33 15L15 33"}
                        initial={{ pathLength: reduceMotion ? 1 : 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: reduceMotion ? 0 : 0.4, delay: reduceMotion ? 0 : 0.12 }}
                      />
                    </svg>
                  </motion.div>
                  <p className="text-lg font-bold">{success ? free ? "Course claimed!" : "Purchase successful!" : "We couldn't confirm your purchase"}</p>
                  <p className="mt-2 break-words text-sm leading-relaxed">{success ? "Your course is now in your library." : error}</p>
                </motion.div>
              ) : (
                <motion.div key="purchase-summary" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduceMotion ? 0 : 0.15 }}>
                <dl className="mt-6 space-y-3 text-sm">
                  {Number(course.price) > price && <div className={`flex justify-between gap-3 ${muted}`}><dt>Original price</dt><dd className="line-through">{money(course.price)}</dd></div>}
                  {Number(course.price) > price && <div className="flex justify-between gap-3 text-emerald-600"><dt>You save</dt><dd>{money(Number(course.price) - price)}</dd></div>}
                  <div className="flex justify-between gap-3 text-lg font-bold"><dt>Total</dt><dd>{free ? "Free" : money(price)}</dd></div>
                  {!free && <>
                    <div className={`flex min-h-[37px] justify-between gap-3 border-t pt-4 ${isDarkMode ? "border-slate-700" : "border-slate-200"} ${muted}`}>
                      <dt>Wallet balance</dt>
                      <dd className="relative min-w-[110px] text-right tabular-nums" aria-live="polite">
                        {checking && <span className="inline-flex items-center gap-2"><span aria-hidden="true" className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" />Checking…</span>}
                        <span className={`block transition-opacity duration-300 motion-reduce:transition-none ${checking ? "absolute inset-0 opacity-0" : "opacity-100"}`} aria-hidden={checking}>{balance === null ? "Unavailable" : money(balance)}</span>
                      </dd>
                    </div>
                    <div className={`flex min-h-[20px] justify-between gap-3 transition-opacity duration-300 motion-reduce:transition-none ${balance !== null && !checking ? "opacity-100" : "opacity-0"} ${insufficient ? "text-amber-600" : muted}`} aria-hidden={balance === null || checking}>
                      <dt>{insufficient ? "Amount needed" : "Balance after purchase"}</dt><dd className="tabular-nums">{balance === null ? "—" : money(Math.abs(balance - price))}</dd>
                    </div>
                  </>}
                </dl>
                </motion.div>
              )}
              </AnimatePresence>
              {!success && insufficient && !checking && <p role="status" className={`mt-4 rounded-xl p-3 text-sm ${isDarkMode ? "bg-amber-900/30 text-amber-200" : "bg-amber-50 text-amber-800"}`}>Your wallet needs {money(price - balance)} more. Add funds using the wallet options, then return to complete your purchase.</p>}
              {error && !purchaseFailed && <div role="alert" className={`mt-4 rounded-xl p-3 text-sm ${isDarkMode ? "bg-red-900/30 text-red-200" : "bg-red-50 text-red-700"}`}>{error}{balance === null && !free && <button type="button" disabled={checking} onClick={() => setWalletAttempt((value) => value + 1)} className="ml-2 font-semibold underline">Check again</button>}</div>}
              </div>
              <div className="flex shrink-0 flex-col gap-2 p-6 pt-4 sm:p-8 sm:pt-4">
                <button type="button" disabled={!success && (busy || checking || (!free && (balance === null || insufficient)))} onClick={success ? () => close(() => onStart({ ...course, purchased: true })) : purchase} className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 dark:bg-red-700 dark:hover:bg-red-800 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                  {busy || checking ? <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none" /> : success ? <FaArrowRight aria-hidden="true" /> : <FaLock aria-hidden="true" />}
                  <span role="status">{success ? "Start learning" : busy ? "Confirming…" : checking ? "Checking wallet…" : free ? "Claim free course" : `Confirm & pay ${money(price)}`}</span>
                </button>
                <button type="button" disabled={busy} onClick={() => close()} className={`min-h-[44px] rounded-xl px-4 py-2 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-40 ${muted}`}>{success ? "Keep browsing" : "Cancel"}</button>
                <p aria-live="polite" className={`min-h-[16px] text-center text-xs ${muted}`}>{busy ? "Please wait while we confirm your course." : ""}</p>
              </div>
            </div>
          </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
    </Transition>
  );
}
